import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FolderOpen,
  Play,
  RefreshCw,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Database,
  Activity,
  TrendingUp,
  Loader2,
  Sparkles,
  Zap,
  BarChart3,
  Target,
  Cpu,
  HardDrive,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Info,
  X,
  Eye,
  Calendar,
  Download,
  ExternalLink
} from 'lucide-react'
import { adminApi, searchApi, specificationsApi } from '../services/api'
import RepositoryConfig from '../components/admin/RepositoryConfig'
import StatisticsDashboard from '../components/admin/StatisticsDashboard'

export default function AdminDashboard() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('overview')
  const [fileListModal, setFileListModal] = useState({ open: false, type: null, title: '' })

  // Fetch repository status
  const { data: repoStatus, isLoading: repoLoading } = useQuery({
    queryKey: ['repositoryStatus'],
    queryFn: () => adminApi.getRepositoryStatus().then(res => res.data),
    refetchInterval: 30000 // Refresh every 30s
  })

  // Fetch processing stats
  const { data: procStats, isLoading: statsLoading } = useQuery({
    queryKey: ['processingStats'],
    queryFn: () => adminApi.getProcessingStats().then(res => res.data),
    refetchInterval: 10000 // Refresh every 10s
  })

  // Fetch specification stats
  const { data: specStats } = useQuery({
    queryKey: ['specificationStats'],
    queryFn: () => adminApi.getSpecificationStats().then(res => res.data),
    refetchInterval: 30000
  })


  // Start processing mutation
  const startProcessing = useMutation({
    mutationFn: (config) => adminApi.startProcessing(config),
    onSuccess: () => {
      queryClient.invalidateQueries(['processingStats'])
      queryClient.invalidateQueries(['repositoryStatus'])
    }
  })

  const handleStartFullScan = () => {
    startProcessing.mutate({
      job_type: 'full_scan',
      job_name: `Full Scan ${new Date().toLocaleString()}`,
      skip_existing: true
    })
  }

  const handleStartIncremental = () => {
    startProcessing.mutate({
      job_type: 'incremental',
      job_name: `Incremental ${new Date().toLocaleString()}`
    })
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Activity },
    { id: 'repository', name: 'Master Folder', icon: FolderOpen },
    { id: 'statistics', name: 'Statistics', icon: TrendingUp }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-purple flex items-center justify-center shadow-lg shadow-primary-500/30">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-success-500 rounded-full border-2 border-white flex items-center justify-center">
                <Sparkles className="w-2 h-2 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Admin Dashboard
              </h1>
              <p className="text-gray-500 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-success-500 animate-pulse" />
                Manage repository and processing jobs
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 animate-fade-in" style={{ animationDelay: '100ms' }}>
          <button
            onClick={handleStartIncremental}
            disabled={startProcessing.isPending || procStats?.active_jobs > 0}
            className="btn btn-secondary group"
            title="Check for new files added since last scan"
          >
            {startProcessing.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
            )}
            Check for New Files
          </button>
          <button
            onClick={handleStartFullScan}
            disabled={startProcessing.isPending || procStats?.active_jobs > 0}
            className="btn btn-primary group relative overflow-hidden"
            title="Re-process all files in the master folder"
          >
            <span className="relative z-10 flex items-center">
              {startProcessing.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2 group-hover:rotate-180 transition-transform duration-500" />
              )}
              Process All Files
            </span>
          </button>
        </div>
      </div>

      {/* Quick Stats - User Friendly */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FileText}
          label="Drawings in Library"
          subtitle="Total PDF files in your master folder"
          value={repoStatus?.total_files || 0}
          loading={repoLoading}
          color="blue"
          delay={0}
        />
        <StatCard
          icon={CheckCircle}
          label="Ready to Search"
          subtitle="Successfully processed and searchable"
          value={procStats?.successful_extractions || 0}
          loading={statsLoading}
          color="green"
          delay={50}
        />
        <StatCard
          icon={AlertTriangle}
          label="Needs Your Attention"
          subtitle="Files that may need manual review"
          value={procStats?.needs_review_count || 0}
          loading={statsLoading}
          color="yellow"
          delay={100}
          clickable={procStats?.needs_review_count > 0}
          onClick={() => procStats?.needs_review_count > 0 && setFileListModal({ open: true, type: 'needs_review', title: 'Files Needing Review' })}
        />
        <StatCard
          icon={XCircle}
          label="Couldn't Process"
          subtitle="Files that failed during extraction"
          value={procStats?.failed_extractions || 0}
          loading={statsLoading}
          color="red"
          delay={150}
          clickable={procStats?.failed_extractions > 0}
          onClick={() => procStats?.failed_extractions > 0 && setFileListModal({ open: true, type: 'failed', title: 'Failed Processing' })}
        />
      </div>

      {/* Tabs */}
      <div className="relative border-b border-gray-200/80 animate-fade-in" style={{ animationDelay: '200ms' }}>
        <nav className="flex space-x-1" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative flex items-center py-3 px-4 font-medium text-sm rounded-t-xl transition-all duration-300
                ${activeTab === tab.id
                  ? 'text-primary-600 bg-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }
              `}
            >
              <tab.icon className={`w-4 h-4 mr-2 transition-transform duration-300 ${activeTab === tab.id ? 'scale-110' : ''}`} />
              {tab.name}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-accent-purple" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="animate-fade-in" style={{ animationDelay: '250ms' }}>
        {activeTab === 'overview' && (
          <OverviewTab
            repoStatus={repoStatus}
            procStats={procStats}
            specStats={specStats}
          />
        )}
        {activeTab === 'repository' && (
          <RepositoryConfig />
        )}
        {activeTab === 'statistics' && (
          <StatisticsDashboard stats={specStats} procStats={procStats} />
        )}
      </div>

      {/* File List Modal */}
      {fileListModal.open && (
        <FileListModal
          type={fileListModal.type}
          title={fileListModal.title}
          onClose={() => setFileListModal({ open: false, type: null, title: '' })}
        />
      )}
    </div>
  )
}

function StatCard({ icon: Icon, label, value, loading, color, delay = 0, subtitle, onClick, clickable }) {
  const colorConfig = {
    blue: {
      bg: 'bg-gradient-to-br from-primary-50 to-primary-100/50',
      icon: 'bg-gradient-to-br from-primary-500 to-primary-600',
      iconShadow: 'shadow-primary-500/30',
      text: 'text-primary-600',
      glow: 'group-hover:shadow-primary-500/20'
    },
    green: {
      bg: 'bg-gradient-to-br from-success-50 to-success-100/50',
      icon: 'bg-gradient-to-br from-success-500 to-success-600',
      iconShadow: 'shadow-success-500/30',
      text: 'text-success-600',
      glow: 'group-hover:shadow-success-500/20'
    },
    yellow: {
      bg: 'bg-gradient-to-br from-warning-50 to-warning-100/50',
      icon: 'bg-gradient-to-br from-warning-500 to-warning-600',
      iconShadow: 'shadow-warning-500/30',
      text: 'text-warning-600',
      glow: 'group-hover:shadow-warning-500/20'
    },
    red: {
      bg: 'bg-gradient-to-br from-danger-50 to-danger-100/50',
      icon: 'bg-gradient-to-br from-danger-500 to-danger-600',
      iconShadow: 'shadow-danger-500/30',
      text: 'text-danger-600',
      glow: 'group-hover:shadow-danger-500/20'
    }
  }

  const config = colorConfig[color]

  return (
    <div
      className={`group card p-6 hover:shadow-xl transition-all duration-300 animate-fade-in-up ${clickable ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
      style={{ animationDelay: `${delay}ms` }}
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className={`relative p-3 rounded-xl ${config.icon} shadow-lg ${config.iconShadow} group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="w-6 h-6 text-white" />
          {/* Glow effect on hover */}
          <div className={`absolute inset-0 rounded-xl ${config.icon} blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-300`} />
        </div>
        <div className="ml-4 flex-1">
          {loading ? (
            <div className="h-8 w-16 bg-gradient-to-r from-gray-200 to-gray-100 rounded animate-pulse" />
          ) : (
            <p className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
          )}
          <p className="text-sm text-gray-700 font-medium">{label}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  )
}

function OverviewTab({ repoStatus, procStats, specStats }) {
  const [showHelp, setShowHelp] = useState(false)

  // Calculate processing percentage
  const processingPercent = repoStatus
    ? ((repoStatus.processed_files / Math.max(1, repoStatus.total_files - (repoStatus.duplicate_files || 0))) * 100)
    : 0

  // Calculate extraction quality metrics
  const highConfidencePercent = procStats?.high_confidence_rate || 0
  const avgConfidence = procStats?.avg_extraction_confidence || 0

  // Determine overall system status for the banner
  const getSystemStatus = () => {
    if (!repoStatus?.is_accessible) {
      return { type: 'error', message: 'Master folder is not accessible. Please check the connection.', icon: XCircle }
    }
    if (procStats?.active_jobs > 0) {
      return { type: 'processing', message: 'Processing files... This may take a while.', icon: Loader2 }
    }
    if (procStats?.needs_review_count > 0) {
      return { type: 'warning', message: `${procStats.needs_review_count} file${procStats.needs_review_count > 1 ? 's' : ''} need your attention. Click "Needs Your Attention" above to review.`, icon: AlertTriangle }
    }
    if (processingPercent >= 100) {
      return { type: 'success', message: 'All files processed! Your library is ready for searching.', icon: CheckCircle }
    }
    if (processingPercent > 0) {
      return { type: 'info', message: `${processingPercent.toFixed(0)}% of your files are processed. Click "Check for New Files" to continue.`, icon: Info }
    }
    return { type: 'info', message: 'Welcome! Configure your master folder and click "Process All Files" to get started.', icon: Info }
  }

  const status = getSystemStatus()
  const statusColors = {
    success: 'bg-success-50 border-success-200 text-success-800',
    warning: 'bg-warning-50 border-warning-200 text-warning-800',
    error: 'bg-danger-50 border-danger-200 text-danger-800',
    info: 'bg-primary-50 border-primary-200 text-primary-800',
    processing: 'bg-accent-cyan/10 border-accent-cyan/30 text-accent-cyan'
  }

  return (
    <div className="space-y-6">
      {/* Status Banner - User Friendly Message */}
      <div className={`p-4 rounded-xl border ${statusColors[status.type]} flex items-center justify-between animate-fade-in`}>
        <div className="flex items-center">
          <status.icon className={`w-5 h-5 mr-3 ${status.type === 'processing' ? 'animate-spin' : ''}`} />
          <span className="font-medium">{status.message}</span>
        </div>
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="flex items-center text-sm opacity-70 hover:opacity-100 transition-opacity"
        >
          <HelpCircle className="w-4 h-4 mr-1" />
          {showHelp ? 'Hide Help' : 'What does this mean?'}
          {showHelp ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
        </button>
      </div>

      {/* Collapsible Help Section */}
      {showHelp && (
        <div className="card p-6 bg-gradient-to-br from-gray-50 to-white animate-fade-in">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center text-gray-800">
              <HelpCircle className="w-5 h-5 mr-2 text-primary-500" />
              Understanding Your Dashboard
            </h3>
            <button onClick={() => setShowHelp(false)} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-primary-50/50 border border-primary-100">
              <p className="font-semibold text-primary-700 flex items-center mb-1">
                <FileText className="w-4 h-4 mr-2" /> Drawings in Library
              </p>
              <p className="text-gray-600">The total number of PDF files in your master folder. These are all the die drawings available for processing.</p>
            </div>
            <div className="p-3 rounded-lg bg-success-50/50 border border-success-100">
              <p className="font-semibold text-success-700 flex items-center mb-1">
                <CheckCircle className="w-4 h-4 mr-2" /> Ready to Search
              </p>
              <p className="text-gray-600">Files that have been successfully analyzed. You can search for similar drawings among these files.</p>
            </div>
            <div className="p-3 rounded-lg bg-warning-50/50 border border-warning-100">
              <p className="font-semibold text-warning-700 flex items-center mb-1">
                <AlertTriangle className="w-4 h-4 mr-2" /> Needs Your Attention
              </p>
              <p className="text-gray-600">Files where the system isn't confident about the extracted data. You may want to verify these manually.</p>
            </div>
            <div className="p-3 rounded-lg bg-danger-50/50 border border-danger-100">
              <p className="font-semibold text-danger-700 flex items-center mb-1">
                <XCircle className="w-4 h-4 mr-2" /> Couldn't Process
              </p>
              <p className="text-gray-600">Files that had errors during processing. These might be corrupted, password-protected, or in an unsupported format.</p>
            </div>
          </div>
          <div className="mt-4 p-3 rounded-lg bg-gray-100 text-gray-600">
            <p className="font-medium mb-1">Quick Tips:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Check for New Files</strong> - Use this when you've added new PDFs to the folder</li>
              <li><strong>Process All Files</strong> - Use this to re-analyze everything from scratch</li>
              <li><strong>Extraction Quality</strong> - Higher percentage means more accurate search results</li>
            </ul>
          </div>
        </div>
      )}

      {/* Repository Status - Full Width Hero Card */}
      <div className="card group hover:shadow-xl transition-all duration-300 overflow-hidden">
        <div className="card-body p-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
            {/* Left - Repository Info */}
            <div className="p-6 lg:col-span-2 bg-gradient-to-br from-white to-gray-50/50">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold flex items-center text-gray-800">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-primary-100 to-primary-50 mr-3">
                    <FolderOpen className="w-5 h-5 text-primary-600" />
                  </div>
                  Your Master Folder
                </h3>
                <span className={`badge ${repoStatus?.is_accessible ? 'badge-success' : 'badge-danger'}`}>
                  {repoStatus?.is_accessible ? 'Connected' : 'Not Connected'}
                </span>
              </div>

              {repoStatus ? (
                <div className="space-y-4">
                  {/* Path */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100">
                    <HardDrive className="w-5 h-5 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Storage Path</p>
                      <p className="font-mono text-sm text-gray-900 truncate">{repoStatus.repository_path}</p>
                    </div>
                    <span className="text-sm font-semibold text-gray-600">{repoStatus.total_size_mb?.toFixed(1)} MB</span>
                  </div>

                  {/* Progress Section */}
                  <div className="p-4 rounded-xl bg-gradient-to-br from-primary-50/50 to-white border border-primary-100">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-gray-700">Processing Progress</span>
                      <span className="text-sm font-bold text-primary-600">
                        {processingPercent.toFixed(0)}%
                      </span>
                    </div>
                    <div className="progress-bar h-3 mb-3">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${processingPercent}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{repoStatus.processed_files} processed</span>
                      <span>{repoStatus.total_files - (repoStatus.duplicate_files || 0)} unique files</span>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 rounded-xl bg-success-50/50 border border-success-100 text-center">
                      <p className="text-2xl font-bold text-success-600">{repoStatus.processed_files}</p>
                      <p className="text-xs text-success-700 font-medium">Ready</p>
                      <p className="text-[10px] text-success-500">Can be searched</p>
                    </div>
                    <div className="p-3 rounded-xl bg-warning-50/50 border border-warning-100 text-center">
                      <p className="text-2xl font-bold text-warning-600">{repoStatus.unprocessed_files || 0}</p>
                      <p className="text-xs text-warning-700 font-medium">Waiting</p>
                      <p className="text-[10px] text-warning-500">Not yet processed</p>
                    </div>
                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-100 text-center">
                      <p className="text-2xl font-bold text-gray-600">{repoStatus.duplicate_files || 0}</p>
                      <p className="text-xs text-gray-500 font-medium">Duplicates</p>
                      <p className="text-[10px] text-gray-400">Identical files skipped</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="animate-pulse space-y-4">
                  <div className="h-16 bg-gradient-to-r from-gray-200 to-gray-100 rounded-xl" />
                  <div className="h-24 bg-gradient-to-r from-gray-200 to-gray-100 rounded-xl" />
                  <div className="grid grid-cols-3 gap-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-20 bg-gradient-to-r from-gray-200 to-gray-100 rounded-xl" />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right - Extraction Quality Gauge */}
            <div className="p-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white flex flex-col justify-center">
              <div className="text-center">
                <div className="relative inline-flex items-center justify-center mb-4">
                  {/* Circular Progress Background */}
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-gray-700"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${(avgConfidence * 100) * 3.52} 352`}
                      strokeLinecap="round"
                      className={`transition-all duration-1000 ${
                        avgConfidence >= 0.8 ? 'text-success-400' :
                        avgConfidence >= 0.6 ? 'text-warning-400' : 'text-danger-400'
                      }`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold">{(avgConfidence * 100).toFixed(0)}%</span>
                    <span className="text-xs text-gray-400">Accuracy Score</span>
                  </div>
                </div>

                <h4 className="text-lg font-semibold mb-1">Search Accuracy</h4>
                <p className="text-gray-400 text-sm mb-4">
                  {highConfidencePercent >= 80 ? 'Excellent - Very reliable results' :
                   highConfidencePercent >= 60 ? 'Good - Results are dependable' :
                   highConfidencePercent >= 40 ? 'Fair - Some results may vary' : 'Improving - Review recommended'}
                </p>

                <div className="flex items-center justify-center gap-2 text-sm">
                  <Target className="w-4 h-4 text-success-400" />
                  <span className="text-gray-300">
                    {highConfidencePercent.toFixed(0)}% of files have reliable data
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row - Performance & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Processing Performance */}
        <div className="card group hover:shadow-xl transition-all duration-300">
          <div className="card-header flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center text-gray-800">
              <div className="p-2 rounded-lg bg-gradient-to-br from-accent-cyan/20 to-accent-cyan/10 mr-3">
                <Cpu className="w-5 h-5 text-accent-cyan" />
              </div>
              System Performance
            </h3>
            <span className="text-xs text-gray-400 flex items-center">
              <TrendingUp className="w-3 h-3 mr-1 text-success-500" />
              Running smoothly
            </span>
          </div>
          <div className="card-body">
            {procStats ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 rounded-lg bg-gray-50/80 hover:bg-gray-100/80 transition-colors">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-gray-400" />
                    <div>
                      <span className="text-gray-700 font-medium">Processing Speed</span>
                      <p className="text-xs text-gray-400">Time to analyze each file</p>
                    </div>
                  </div>
                  <span className="font-semibold text-gray-800 bg-white px-3 py-1 rounded-full border border-gray-100">
                    {procStats.avg_processing_time_ms?.toFixed(0) || 0} ms
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-gray-50/80 hover:bg-gray-100/80 transition-colors">
                  <div className="flex items-center">
                    <BarChart3 className="w-4 h-4 mr-2 text-gray-400" />
                    <div>
                      <span className="text-gray-700 font-medium">Scanned Images</span>
                      <p className="text-xs text-gray-400">Files processed with image recognition</p>
                    </div>
                  </div>
                  <span className="font-semibold text-gray-800">{procStats.ocr_used_count || 0} files</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-gray-50/80 hover:bg-gray-100/80 transition-colors">
                  <div className="flex items-center">
                    <Activity className="w-4 h-4 mr-2 text-gray-400" />
                    <div>
                      <span className="text-gray-700 font-medium">Total Work Done</span>
                      <p className="text-xs text-gray-400">Cumulative processing time</p>
                    </div>
                  </div>
                  <span className="font-semibold text-gray-800">{procStats.total_processing_time_hours?.toFixed(2) || 0} hrs</span>
                </div>
                {procStats.failed_extractions > 0 && (
                  <div className="flex justify-between items-center p-3 rounded-lg bg-danger-50/50 hover:bg-danger-100/50 transition-colors cursor-pointer">
                    <div className="flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-2 text-danger-400" />
                      <div>
                        <span className="text-danger-700 font-medium">Problem Files</span>
                        <p className="text-xs text-danger-400">Click to see what went wrong</p>
                      </div>
                    </div>
                    <span className="font-semibold text-danger-600 bg-danger-100 px-3 py-1 rounded-full">
                      {procStats.failed_extractions}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-12 bg-gradient-to-r from-gray-200 to-gray-100 rounded-lg" />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions & Activity */}
        <div className="card group hover:shadow-xl transition-all duration-300">
          <div className="card-header flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center text-gray-800">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary-100 to-accent-purple/20 mr-3">
                <Zap className="w-5 h-5 text-primary-600" />
              </div>
              Quick Actions
            </h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 gap-3">
              <button className="btn btn-secondary justify-center group hover:shadow-md h-16 flex-col">
                <RefreshCw className="w-5 h-5 mb-1 group-hover:rotate-180 transition-transform duration-500" />
                <span className="text-xs">Update Numbers</span>
              </button>
              <button className="btn btn-secondary justify-center group hover:shadow-md h-16 flex-col">
                <Database className="w-5 h-5 mb-1 group-hover:scale-110 transition-transform" />
                <span className="text-xs">Speed Up Search</span>
              </button>
              <button className="btn btn-secondary justify-center group hover:shadow-md h-16 flex-col">
                <FileText className="w-5 h-5 mb-1 group-hover:scale-110 transition-transform" />
                <span className="text-xs">Download Report</span>
              </button>
              <button className="btn btn-secondary justify-center group hover:shadow-md h-16 flex-col relative">
                <AlertTriangle className="w-5 h-5 mb-1 group-hover:scale-110 transition-transform" />
                <span className="text-xs">View Problems</span>
                {procStats?.needs_review_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-warning-500 text-white text-xs rounded-full flex items-center justify-center">
                    {procStats.needs_review_count}
                  </span>
                )}
              </button>
            </div>

            {/* Last Activity Section */}
            <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                Recent Activity
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Files processed today</span>
                  <span className="font-semibold text-success-600 bg-success-50 px-2 py-0.5 rounded-full">
                    {procStats?.files_processed_today || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Files this week</span>
                  <span className="font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                    {procStats?.files_processed_this_week || 0}
                  </span>
                </div>
                {procStats?.last_processing_time && (
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                    <span className="text-gray-600">Last processed</span>
                    <span className="text-xs text-gray-500">
                      {new Date(procStats.last_processing_time).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* AI Info */}
            <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-primary-50 to-accent-purple/10 border border-primary-100">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-primary-500" />
                <div>
                  <p className="text-sm font-medium text-gray-800">AI-Powered Extraction</p>
                  <p className="text-xs text-gray-500">Your files are analyzed with smart technology for accurate search results</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FileListModal({ type, title, onClose }) {
  const [downloading, setDownloading] = useState({})

  // Fetch files based on type
  const { data: files, isLoading, error } = useQuery({
    queryKey: ['fileList', type],
    queryFn: async () => {
      if (type === 'needs_review') {
        const response = await specificationsApi.list({ needs_review: true, page_size: 100 })
        return response.data.items || response.data || []
      } else if (type === 'failed') {
        const response = await specificationsApi.list({ status: 'FAILED', page_size: 100 })
        return response.data.items || response.data || []
      }
      return []
    },
    enabled: !!type
  })

  const handleDownload = async (filePath) => {
    if (!filePath || downloading[filePath]) return

    const isAzurePath = filePath.startsWith('azure://')
    if (!isAzurePath) return

    setDownloading(prev => ({ ...prev, [filePath]: true }))
    try {
      const response = await searchApi.getDownloadUrl(filePath)
      const downloadUrl = response.data.download_url
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Failed to get download URL:', error)
      alert('Failed to download PDF. Please try again.')
    } finally {
      setDownloading(prev => ({ ...prev, [filePath]: false }))
    }
  }

  const getFileName = (filePath) => {
    if (!filePath) return 'Unknown file'
    // Handle Azure paths
    if (filePath.startsWith('azure://')) {
      const parts = filePath.split('/')
      return parts[parts.length - 1]
    }
    // Handle regular paths
    const parts = filePath.split(/[/\\]/)
    return parts[parts.length - 1]
  }

  const getStatusBadge = (spec) => {
    if (type === 'needs_review') {
      const confidence = spec.extraction_confidence || 0
      if (confidence < 0.5) {
        return <span className="badge badge-danger text-xs">Low Confidence ({(confidence * 100).toFixed(0)}%)</span>
      }
      return <span className="badge badge-warning text-xs">Needs Review ({(confidence * 100).toFixed(0)}%)</span>
    }
    if (type === 'failed') {
      return <span className="badge badge-danger text-xs">Failed</span>
    }
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-fade-in">
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${
          type === 'needs_review' ? 'bg-warning-500' : 'bg-danger-500'
        } text-white`}>
          <div className="flex items-center">
            {type === 'needs_review' ? (
              <AlertTriangle className="w-5 h-5 mr-3" />
            ) : (
              <XCircle className="w-5 h-5 mr-3" />
            )}
            <div>
              <h2 className="text-lg font-bold">{title}</h2>
              <p className="text-sm opacity-80">
                {files?.length || 0} file{files?.length !== 1 ? 's' : ''} found
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-140px)]">
          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Loading files...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <XCircle className="w-8 h-8 mx-auto text-danger-400" />
              <p className="text-danger-600 mt-2">Failed to load files</p>
              <p className="text-gray-400 text-sm">{error.message}</p>
            </div>
          ) : files?.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="w-8 h-8 mx-auto text-success-400" />
              <p className="text-gray-600 mt-2">No files found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {files?.map((spec, index) => {
                const filePath = spec.file_path || spec.source_pdf_path
                const isAzurePath = filePath?.startsWith('azure://')

                return (
                  <div
                    key={spec.id || index}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-gray-900 truncate">
                            {getFileName(filePath)}
                          </span>
                          {getStatusBadge(spec)}
                        </div>
                        {spec.section_number && (
                          <p className="text-sm text-gray-500 ml-6">
                            Section: {spec.section_number}
                          </p>
                        )}
                        {spec.extraction_notes && (
                          <p className="text-xs text-gray-400 ml-6 mt-1 line-clamp-2">
                            {spec.extraction_notes}
                          </p>
                        )}
                      </div>
                      {isAzurePath && (
                        <button
                          onClick={() => handleDownload(filePath)}
                          disabled={downloading[filePath]}
                          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 transition-colors flex-shrink-0"
                        >
                          {downloading[filePath] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          <span>View PDF</span>
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

